/**
 * The adapter between the client's log buffer and `@annwn/ui`'s `LogPanel`
 * (RC1-05).
 *
 * Pure, and split from the island the way `condition-view-model.ts` is split
 * from the condition overlay: the interesting decision — how the client's six
 * reader-aware classes collapse onto the library's tone vocabulary — is a
 * function of a `LogLine`, testable without a browser or a React root.
 *
 * The library owns a small, deliberately coarse set of tones; the client owns a
 * richer notion of *who a line is about*. The one distinction worth carrying
 * across the seam is "was that me being hit" — `against-me` becomes `damage`,
 * the red that means *you* — and speech keeps its own voice. `mine` and
 * `nearby` read as ordinary world lines; the "was that me" answer the player
 * actually asks lives on the body diagram and the against-me red, not on a
 * fourth shade of combat text.
 */

import type { LogEntry, LogTone } from '@annwn/ui';

import type { LogClass, LogLine } from './log-pane.js';

export function toneFor(kind: LogClass): LogTone {
  switch (kind) {
    case 'system':
      return 'system';
    case 'against-me':
      return 'damage';
    case 'speech':
      return 'speech';
    default:
      // world, mine, nearby — ordinary world prose.
      return 'normal';
  }
}

/** One buffer line as a `LogPanel` entry. `actors` is carried only when present. */
export function toEntry(line: LogLine): LogEntry {
  return {
    id: String(line.id),
    timestamp: line.stamp,
    text: line.text,
    tone: toneFor(line.kind),
    ...(line.actors.length > 0 ? { actors: line.actors } : {}),
  };
}
